import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DirectoryRegion } from '@directory-of-directories/domain';
import { skip } from 'rxjs';
import {
  DirectoryToolbarState,
  DirectoryToolbarStateService,
} from './directory-toolbar-state.service';

@Injectable({ providedIn: 'root' })
export class DirectoryToolbarUrlSyncService {
  private readonly _stateService = inject(DirectoryToolbarStateService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    // On load: URL → state
    const params = this._route.snapshot.queryParams;
    this._stateService.update(this.paramsToState(params), true);

    // State → URL
    this._stateService.urlState$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((state) => {
        const rawParams = this.stateToParams(state);
        // remove null values so Router removes params when needed
        const queryParams: Params = Object.fromEntries(
          Object.entries(rawParams).filter(([, v]) => v != null),
        );
        this._router.navigate([], {
          relativeTo: this._route,
          queryParams,
          replaceUrl: true,
          fragment: 'searchResults',
        });
      });
  }

  private stateToParams(state: DirectoryToolbarState): Record<string, string | null> {
    const regionMap = { US: 'us', EU: 'eu' } as const;
    const regionKey = regionMap[state.currentRegion];

    return {
      search: state.search || null,
      sort: state.sorting.toString(),
      categories: state.skill_categories.length ? state.skill_categories.join(';') : null,
      region: state.currentRegion,
      page: state[regionKey].page.toString(),
    };
  }

  private paramsToState(params: Params): Partial<DirectoryToolbarState> {
    const rawRegion = params['region'] as string;
    const region: DirectoryRegion = rawRegion === 'EU' ? 'EU' : 'US';
    const regionKey = region === 'EU' ? 'eu' : 'us';

    const rawCategories = params['categories'] as string;
    const skillCategories =
      rawCategories && rawCategories !== 'null' ? rawCategories.split(';') : [];

    const rawSearch = params['search'] as string;
    const search = rawSearch && rawSearch !== 'null' ? rawSearch : '';

    return {
      search,
      sorting: Number(params['sort']) || 0,
      skill_categories: skillCategories,
      currentRegion: region,
      [regionKey]: { page: Number(params['page']) || 1 },
    };
  }
}
